// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file TabList.tsx
 * @input Uses React, StyleX, TabListContext, useListFocus, useKeyboardHint
 * @output Exports TabList component and TabListProps type
 * @position Nav wrapper; provides TabListContext to Tab and TabMenu children.
 *   Owns roving-tabindex keyboard navigation (Arrow/Home/End) across the tab
 *   strip via the shared useListFocus hook so it is a single Tab stop.
 *
 * SYNC: When modified, update:
 * - /packages/core/src/TabList/TabList.doc.mjs
 * - /packages/core/src/TabList/index.ts
 * - /packages/core/src/TabList/TabList.test.tsx
 * - /packages/cli/templates/blocks/components/TabList/ (showcase blocks)
 */
import React, { useCallback, useMemo } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { borderVars, colorVars, spacingVars } from "../theme/tokens.stylex.js";
import { TabListContext } from "./TabListContext.js";
import { useSize } from "../SizeContext/SizeContext.js";
import { mergeProps, mergeRefs } from "../utils/index.js";
import { useListFocus } from "../hooks/useListFocus.js";
import { useKeyboardHint } from "../hooks/useKeyboardHint.js";
import { EDGE_COMP_ATTR } from "../Layout/edgeCompensation.stylex.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";

/**
 * Selector matching the focusable stops in the tab strip: every Tab
 * (`[data-tab-value]`) and every TabMenu trigger (`[data-tab-menu]`),
 * in DOM order. Disabled stops are filtered out by the handler.
 */
import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
const TAB_STOP_SELECTOR = '[data-tab-value],[data-tab-menu]';
const styles = {
  nav: {
    k1xSpc: "x78zum5",
    kGNEyG: "x1qjc9v5",
    kOIVth: "x1lsbc85",
    ks0D6T: "x193iq5w",
    k7Eaqz: "xeuugli",
    $$css: true
  },
  fill: {
    kzqmXN: "xh8yej3",
    $$css: true
  },
  divider: {
    kt9PQ7: "x92x3c3",
    kfdmCh: "x1q0q8m5",
    kL6WhQ: "xw8gpjh",
    $$css: true
  }
};

/**
 * Tab navigation wrapper. Provides context for value/onChange/size
 * to Tab and TabMenu children.
 *
 * @example
 * ```
 * <TabList value={activeTab} onChange={setActiveTab}>
 *   <Tab value="home" label="Home" />
 *   <Tab value="settings" label="Settings" />
 *   <TabMenu label="More">
 *     <Tab value="analytics" label="Analytics" />
 *     <Tab value="reports" label="Reports" />
 *   </TabMenu>
 * </TabList>
 * ```
 */
export function TabList({
  ref,
  value,
  onChange,
  size: sizeProp,
  layout = 'hug',
  hasDivider = false,
  orientation = 'horizontal',
  xstyle,
  className,
  style,
  children,
  onKeyDown: onKeyDownProp,
  onFocus: onFocusProp,
  onBlur: onBlurProp,
  'aria-label': ariaLabelFromProps,
  'aria-orientation': _ariaOrientation,
  [EDGE_COMP_ATTR]: _edgeCompAttr,
  ...restProps
}) {
  const t = useTranslator();
  const ariaLabel = ariaLabelFromProps ?? t('@astryx.tabList.label');
  const size = useSize(sizeProp, 'md');

  // Roving-tabindex keyboard navigation across the tab strip via the shared
  // hook. `orientation: 'both'` accepts both arrow axes per the WAI-ARIA APG
  // allowance for tab strips (ArrowRight/ArrowDown advance, ArrowLeft/ArrowUp
  // retreat) regardless of the component's `orientation` prop, which only
  // drives the keyboard hint badge (see useKeyboardHint below). We do not set
  // `aria-orientation` on the `<nav>`: that attribute is invalid on the
  // navigation role and triggers an axe `aria-allowed-attr` violation.
  //
  // `hasRovingTabIndex` makes the hook own the single tab stop: it stamps
  // tabindex 0/-1, repairs the stop on mount and as stops mount/unmount or
  // toggle disabled, and — via `handleFocus` on the nav — keeps the stop in
  // sync after clicks or programmatic focus. Individual Tabs still render
  // `tabIndex={isSelected ? 0 : -1}` (see Tab.tsx) as the initial source of
  // truth; the hook's repair preserves an existing tab stop and only promotes
  // the first enabled stop when none is tabbable.
  const {
    listRef,
    handleKeyDown,
    handleFocus
  } = useListFocus({
    itemSelector: TAB_STOP_SELECTOR,
    orientation: 'both',
    hasRovingTabIndex: true
  });
  const {
    hintElement,
    onKeyDown: onHintKeyDown,
    onFocus: onHintFocus,
    onBlur: onHintBlur
  } = useKeyboardHint({
    orientation
  });
  const contextValue = useMemo(() => ({
    value,
    onChange,
    size,
    layout
  }), [value, onChange, size, layout]);
  const handleRootKeyDown = useCallback(e => {
    onKeyDownProp?.(e);
    if (e.defaultPrevented) {
      return;
    }
    onHintKeyDown(e);
    handleKeyDown(e);
  }, [onKeyDownProp, onHintKeyDown, handleKeyDown]);
  const handleRootFocus = useCallback(e => {
    onFocusProp?.(e);
    if (e.defaultPrevented) {
      return;
    }
    onHintFocus(e);
    handleFocus(e);
  }, [onFocusProp, onHintFocus, handleFocus]);
  const handleRootBlur = useCallback(e => {
    onBlurProp?.(e);
    if (e.defaultPrevented) {
      return;
    }
    onHintBlur(e);
  }, [onBlurProp, onHintBlur]);
  return /*#__PURE__*/_jsx(TabListContext, {
    value: contextValue,
    children: /*#__PURE__*/_jsxs("nav", {
      ref: mergeRefs(ref, listRef),
      ...restProps,
      "aria-label": ariaLabel,
      onKeyDown: handleRootKeyDown,
      onFocus: handleRootFocus,
      onBlur: handleRootBlur,
      [EDGE_COMP_ATTR]: '',
      ...mergeProps(themeProps('tab-list', {
        size
      }), stylex.props(styles.nav, layout === 'fill' && styles.fill, hasDivider && styles.divider, xstyle), className, style),
      children: [children, hintElement]
    })
  });
}
TabList.displayName = 'TabList';